import { ReactNode, useEffect, useMemo } from 'react'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import {
  formatDateToPtBr,
  initialQueryState,
  OptionsHelper,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  DateInput,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: "Nova importação"). */
  actions?: ReactNode
}

export const ImportacaoListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 300)

  // Período fechado: início a partir de 00:00 do dia e término até 23:59:59 do dia.
  const search = useMemo(
    () =>
      [
        debouncedFilters.empresa ? `empresa:${debouncedFilters.empresa.value}` : '',
        debouncedFilters.dataInicio ? `inicio>${debouncedFilters.dataInicio}T00:00:00` : '',
        debouncedFilters.dataTermino ? `termino<'${debouncedFilters.dataTermino}T23:59:59` : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo. Datas puras via split
  // (formatDateToPtBr), sem `new Date`, para não voltar um dia no fuso do Brasil.
  const activeFilters: ActiveFilter[] = [
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
    filters.dataInicio && {
      key: 'dataInicio',
      label: 'A partir de',
      value: formatDateToPtBr(filters.dataInicio),
      onRemove: () => setFilter('dataInicio', ''),
    },
    filters.dataTermino && {
      key: 'dataTermino',
      label: 'Até',
      value: formatDateToPtBr(filters.dataTermino),
      onRemove: () => setFilter('dataTermino', ''),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <TableFilters
      actions={actions}
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'importação encontrada' : 'importações encontradas'}`
          }
        />
      }
    >
      <TableFilter label={t('empresa.label')} wide>
        <CustomSelect
          entity='empresa'
          onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          value={filters.empresa}
        />
      </TableFilter>
      <TableFilter label='Data início'>
        <DateInput
          value={filters.dataInicio}
          onChange={(e) => setFilter('dataInicio', e.target.value)}
        />
      </TableFilter>
      <TableFilter label='Data término'>
        <DateInput
          value={filters.dataTermino}
          onChange={(e) => setFilter('dataTermino', e.target.value)}
        />
      </TableFilter>
    </TableFilters>
  )
}
